import {
  ARRAY_PATH,
  NEW_CONDITION_OPTION,
  SIDE_OF_BODY_VALUES,
} from '../constants';
import { conditionObjects } from '../content/conditionOptions';

// Lighthouse rejects a claim that submits the same disability name twice, so
// these helpers compare the names vets-api will submit, not the strings the
// Veteran typed. vets-api scrubs disallowed characters from a free-text
// condition and appends the side of body, so "ankle sprain" with a side of LEFT
// is submitted as "ankle sprain, Left". A rated disability claimed for an
// increase is forwarded verbatim.
const CHARACTERS_VETS_API_DROPS = /[^a-zA-Z0-9'.,/() -]/g;
const NON_ALPHANUMERIC = /[^a-z0-9]/g;

export const scrubConditionName = name =>
  String(name ?? '')
    .replace(CHARACTERS_VETS_API_DROPS, '')
    .replace(/\s+/g, ' ')
    .trim();

// The name Lighthouse compares, plus a punctuation-insensitive form so a hyphen
// or period alone doesn't hide a duplicate. Empty when the name has no letters
// or digits.
const nameKeys = name => {
  const strict = name.toLowerCase();
  const loose = strict.replace(NON_ALPHANUMERIC, '');

  return loose ? [strict, loose] : [];
};

export const hasNameCollision = (claimedKeys, keys) =>
  keys.some(key => claimedKeys.has(key));

const ratedDisabilitySubmittedName = name => String(name ?? '').trim();

// The name a new condition is submitted under. The side is lowercased to match
// createNewConditionName; every comparison here is case-insensitive.
const newConditionSubmittedName = (condition, sideOfBody) => {
  const scrubbed = scrubConditionName(condition);

  // vets-api submits a blank name rather than laterality on its own.
  if (!scrubbed) {
    return '';
  }

  const side = String(sideOfBody ?? '').trim();

  return side ? `${scrubbed}, ${side.toLowerCase()}` : scrubbed;
};

export const ratedDisabilityNameKeys = name =>
  nameKeys(ratedDisabilitySubmittedName(name));

export const newConditionNameKeys = (condition, sideOfBody) =>
  nameKeys(newConditionSubmittedName(condition, sideOfBody));

const isIncreaseRow = row =>
  Boolean(row?.ratedDisability) && row.ratedDisability !== NEW_CONDITION_OPTION;

// The disability name a row of the conditions array will be submitted under.
const conditionRowSubmittedName = row =>
  isIncreaseRow(row)
    ? ratedDisabilitySubmittedName(row.ratedDisability)
    : newConditionSubmittedName(row?.condition, row?.sideOfBody);

// The name keys a row of the conditions array will be submitted under.
const conditionRowNameKeys = row => nameKeys(conditionRowSubmittedName(row));

// `index` arrives as a number from the array builder and as a string from the
// URL. Anything else (null on non-array pages) must not match a real index.
const toIndex = value =>
  typeof value === 'number' ? value : Number.parseInt(value, 10);

const collectNameKeys = (formData, currentIndex, rowNameKeys) => {
  const rows = Array.isArray(formData?.[ARRAY_PATH])
    ? formData[ARRAY_PATH]
    : [];
  const skipIndex = toIndex(currentIndex);

  return rows.reduce((keys, row, index) => {
    if (index !== skipIndex) {
      rowNameKeys(row).forEach(key => keys.add(key));
    }
    return keys;
  }, new Set());
};

// Name keys claimed by every condition except the one at `currentIndex`. Rated
// disabilities not added to the claim are excluded: they submit with an action
// type of NONE and are dropped before Lighthouse sees them.
const getClaimedNameKeys = (formData, currentIndex) =>
  collectNameKeys(formData, currentIndex, conditionRowNameKeys);

// Only the new conditions, for deciding which rated disabilities can still be
// claimed for an increase.
export const getNewConditionNameKeys = (formData, currentIndex) =>
  collectNameKeys(formData, currentIndex, row =>
    isIncreaseRow(row) ? [] : conditionRowNameKeys(row),
  );

// How many conditions other than the one at `currentIndex` would be submitted
// under one of `candidateKeys`.
const countMatchingRows = (formData, currentIndex, candidateKeys) => {
  const rows = Array.isArray(formData?.[ARRAY_PATH])
    ? formData[ARRAY_PATH]
    : [];
  const skipIndex = toIndex(currentIndex);
  const candidates = new Set(candidateKeys);

  return rows.reduce(
    (count, row, index) =>
      index !== skipIndex &&
      hasNameCollision(candidates, conditionRowNameKeys(row))
        ? count + 1
        : count,
    0,
  );
};

export const conditionHasSideOfBody = condition =>
  Boolean(
    conditionObjects.find(({ option }) => option === condition)?.sideOfBody,
  );

// True when `condition` would duplicate a condition already on the claim.
// `currentIndex` is the row being added or edited; when it doesn't identify a
// row, that row is counted among the matches, so it takes two to be a
// duplicate.
export const isDuplicateConditionName = (formData, currentIndex, condition) => {
  const candidateKeys = newConditionNameKeys(condition);

  if (!candidateKeys.length) {
    return false;
  }

  const threshold = Number.isInteger(toIndex(currentIndex)) ? 0 : 1;

  // The side of body is picked on the next page, so a lateral condition is only
  // a duplicate once every side it could be submitted under is taken.
  if (conditionHasSideOfBody(condition)) {
    return SIDE_OF_BODY_VALUES.every(
      side =>
        countMatchingRows(
          formData,
          currentIndex,
          newConditionNameKeys(condition, side),
        ) > threshold,
    );
  }

  return countMatchingRows(formData, currentIndex, candidateKeys) > threshold;
};

// Sides of body that wouldn't duplicate a name already on the claim.
// `currentSide` is always kept so a saved answer is never dropped. Falls back to
// every side when they all collide, so the required radio is never empty.
export const getAvailableSidesOfBody = (
  formData,
  currentIndex,
  condition,
  currentSide,
) => {
  const claimed = getClaimedNameKeys(formData, currentIndex);
  const available = SIDE_OF_BODY_VALUES.filter(
    side =>
      side === currentSide ||
      !hasNameCollision(claimed, newConditionNameKeys(condition, side)),
  );

  return available.length ? available : SIDE_OF_BODY_VALUES;
};

// Names that more than one condition would be submitted under, in row order.
// Backstop for rows the item pages never checked, such as a resumed in-progress
// form. Groups on the exact submitted name so it never blocks a claim
// Lighthouse would accept.
export const getDuplicateConditionNames = formData => {
  const rows = Array.isArray(formData?.[ARRAY_PATH])
    ? formData[ARRAY_PATH]
    : [];

  const byName = rows.reduce((names, row) => {
    const name = conditionRowSubmittedName(row);

    if (!name) {
      return names;
    }

    const key = name.toLowerCase();
    const seen = names.get(key);

    if (seen) {
      seen.count += 1;
    } else {
      names.set(key, { name, count: 1 });
    }

    return names;
  }, new Map());

  return [...byName.values()]
    .filter(({ count }) => count > 1)
    .map(({ name }) => name);
};
